import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  AlertOctagon, AlertTriangle, CheckCircle2, Download, PlayCircle,
  RefreshCw, Search, Activity,
} from 'lucide-react';

const SEVERITY_META = {
  blocker: { label: 'Blocker', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: AlertOctagon },
  warn:    { label: 'Warn',    cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle },
  ok:      { label: 'OK',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
};

const SeverityBadge = ({ s }) => {
  const m = SEVERITY_META[s] || SEVERITY_META.ok;
  const Icon = m.Icon;
  return (
    <span
      data-testid={`drift-severity-${s}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`}
    >
      <Icon className="h-3 w-3" />
      {m.label}
    </span>
  );
};

const fmtINR = (v) => {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toFixed(2)}`;
};

export default function AdminSSOTDriftWatchlist() {
  const { apiCall, token } = useAuth();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0, pages: 1 });
  const [counts, setCounts] = useState({ ok: 0, warn: 0, blocker: 0 });
  const [lastSweep, setLastSweep] = useState(null);
  const [thresholds, setThresholds] = useState({ blocker_rupees: 10, warn_rupees: 1 });
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [reauditingId, setReauditingId] = useState(null);

  // Filters
  const [severity, setSeverity] = useState('actionable'); // actionable=warn+blocker (default)
  const [sinceHours, setSinceHours] = useState(168);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        since_hours: String(sinceHours),
        page: String(page),
        page_size: '50',
      });
      if (severity !== 'actionable') qs.set('severity', severity);
      if (search.trim()) qs.set('q', search.trim());
      const data = await apiCall(`/api/admin/financial-ssot/drift-watchlist?${qs}`);
      setRows(data.rows || []);
      setPagination(data.pagination || { page: 1, page_size: 50, total: 0, pages: 1 });
      setCounts(data.counts_in_window || { ok: 0, warn: 0, blocker: 0 });
      setLastSweep(data.last_sweep || null);
      setThresholds(data.thresholds || { blocker_rupees: 10, warn_rupees: 1 });
    } catch (e) {
      toast.error('Failed to load drift watchlist');
    } finally {
      setLoading(false);
    }
  }, [apiCall, severity, sinceHours, page, search]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const runNow = async () => {
    if (running) return;
    setRunning(true);
    try {
      const r = await apiCall(
        `/api/admin/financial-ssot/drift-watchlist/run-now?lookback_hours=${sinceHours}&notify=true`,
        { method: 'POST' }
      );
      const c = r?.counts || {};
      toast.success(
        `Sweep done — scanned ${r?.scanned ?? 0}: ${c.blocker || 0} blocker · ${c.warn || 0} warn · ${c.ok || 0} ok`,
        { duration: 6000 }
      );
      setPage(1);
      await fetchRows();
    } catch (e) {
      toast.error('Drift sweep failed');
    } finally {
      setRunning(false);
    }
  };

  const reaudit = async (orderId) => {
    if (!orderId) return;
    setReauditingId(orderId);
    try {
      const r = await apiCall(
        `/api/admin/financial-ssot/drift-watchlist/reaudit/${encodeURIComponent(orderId)}`,
        { method: 'POST' }
      );
      toast.success(`Re-audited — severity: ${r?.severity || 'unknown'}`);
      await fetchRows();
    } catch (e) {
      toast.error('Re-audit failed');
    } finally {
      setReauditingId(null);
    }
  };

  const exportCsv = async () => {
    try {
      const qs = new URLSearchParams({ since_hours: String(sinceHours) });
      if (severity !== 'actionable') qs.set('severity', severity);
      if (search.trim()) qs.set('q', search.trim());
      const backend = process.env.REACT_APP_BACKEND_URL;
      const url = `${backend}/api/admin/financial-ssot/drift-watchlist/export.csv?${qs}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('CSV download failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `drift-watchlist-${sinceHours}h.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error('CSV export failed');
    }
  };

  const lastSweepRel = lastSweep?.finished_at
    ? new Date(lastSweep.finished_at).toLocaleString()
    : 'No sweep yet';

  return (
    <div className="space-y-6" data-testid="drift-watchlist-tab">
      {/* Hero summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-rose-200 bg-rose-50/30" data-testid="drift-tile-blocker">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <AlertOctagon className="h-3 w-3 text-rose-500" /> Blockers
            </CardDescription>
            <CardTitle className="text-3xl text-rose-700">{counts.blocker || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-amber-200 bg-amber-50/30" data-testid="drift-tile-warn">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-amber-500" /> Warnings
            </CardDescription>
            <CardTitle className="text-3xl text-amber-700">{counts.warn || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-emerald-200" data-testid="drift-tile-ok">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Clean
            </CardDescription>
            <CardTitle className="text-3xl text-emerald-700">{counts.ok || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="drift-tile-last-sweep">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Activity className="h-3 w-3 text-indigo-500" /> Last sweep
            </CardDescription>
            <CardTitle className="text-base font-mono leading-tight">{lastSweepRel}</CardTitle>
            <p className="text-[11px] text-gray-500 mt-1">
              Scanned {lastSweep?.scanned ?? '—'} · {lastSweep?.lookback_hours ?? '—'}h window
            </p>
          </CardHeader>
        </Card>
      </div>

      {/* Toolbar */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-gray-500 block mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                placeholder="Order ID, Shopify ID, vendor ID…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-7 h-9"
                data-testid="drift-search-input"
              />
            </div>
          </div>
          <div className="w-40">
            <label className="text-xs text-gray-500 block mb-1">Severity</label>
            <Select value={severity} onValueChange={(v) => { setSeverity(v); setPage(1); }}>
              <SelectTrigger className="h-9" data-testid="drift-severity-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="actionable">Warn + Blocker</SelectItem>
                <SelectItem value="blocker">Blocker only</SelectItem>
                <SelectItem value="warn">Warn only</SelectItem>
                <SelectItem value="ok">OK only</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <label className="text-xs text-gray-500 block mb-1">Window</label>
            <Select
              value={String(sinceHours)}
              onValueChange={(v) => { setSinceHours(Number(v)); setPage(1); }}
            >
              <SelectTrigger className="h-9" data-testid="drift-window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24">Last 24h</SelectItem>
                <SelectItem value="72">Last 3 days</SelectItem>
                <SelectItem value="168">Last 7 days</SelectItem>
                <SelectItem value="336">Last 14 days</SelectItem>
                <SelectItem value="720">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={fetchRows}
              disabled={loading}
              data-testid="drift-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={loading || rows.length === 0}
              data-testid="drift-export-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              CSV
            </Button>
            <Button
              onClick={runNow}
              disabled={running}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="drift-run-now-btn"
            >
              <PlayCircle className={`h-4 w-4 mr-2 ${running ? 'animate-pulse' : ''}`} />
              {running ? 'Sweeping…' : 'Run sweep now'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Threshold legend */}
      <p className="text-xs text-gray-500 -mt-2">
        Severity: <span className="text-rose-700 font-medium">blocker</span> when max |Δ| ≥ ₹
        {Number(thresholds.blocker_rupees).toFixed(0)}, <span className="text-amber-700 font-medium">warn</span>{' '}
        when ≥ ₹{Number(thresholds.warn_rupees).toFixed(0)}, otherwise <span className="text-emerald-700 font-medium">ok</span>.
      </p>

      {/* Results table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {pagination.total} order{pagination.total === 1 ? '' : 's'} flagged
            <span className="text-xs text-gray-400 font-normal ml-2">
              (page {pagination.page} of {pagination.pages || 1})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-y border-gray-200">
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2">Severity</th>
                  <th className="px-4 py-2">Order</th>
                  <th className="px-4 py-2">Vendor</th>
                  <th className="px-4 py-2 text-right">Max |Δ|</th>
                  <th className="px-4 py-2 text-right">Shopify</th>
                  <th className="px-4 py-2 text-right">Invoice</th>
                  <th className="px-4 py-2 text-right">Razorpay</th>
                  <th className="px-4 py-2 text-right">Payout net</th>
                  <th className="px-4 py-2">Reasons</th>
                  <th className="px-4 py-2">Audited</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-gray-400 text-sm">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-gray-400 text-sm" data-testid="drift-empty-state">
                      No drift rows in this window. Try widening the window or hit "Run sweep now".
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr
                    key={r.order_id}
                    className="border-b border-gray-100 hover:bg-gray-50/60"
                    data-testid={`drift-row-${r.order_id}`}
                  >
                    <td className="px-4 py-3"><SeverityBadge s={r.severity} /></td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-gray-700">{(r.order_id || '').slice(0, 12)}</div>
                      <div className="text-[11px] text-gray-400">{r.shopify_order_id || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <span className="font-mono text-xs">{(r.vendor_id || '—').slice(0, 12)}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-rose-700 font-semibold">
                      {fmtINR(r.max_abs_delta)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">{fmtINR(r.shopify_total)}</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">{fmtINR(r.invoice_grand_total)}</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">{fmtINR(r.razorpay_amount)}</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">{fmtINR(r.payout_net)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[300px]">
                        {(r.reasons || []).slice(0, 3).map((rs, i) => (
                          <Badge
                            key={i}
                            variant="outline"
                            className="text-[10px] font-normal bg-gray-50 text-gray-600"
                          >
                            {rs}
                          </Badge>
                        ))}
                        {(r.reasons || []).length > 3 && (
                          <span className="text-[10px] text-gray-400">+{r.reasons.length - 3} more</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {r.last_audited_at ? new Date(r.last_audited_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => reaudit(r.order_id)}
                        disabled={reauditingId === r.order_id}
                        data-testid={`drift-reaudit-btn-${r.order_id}`}
                      >
                        <RefreshCw
                          className={`h-3 w-3 mr-1 ${reauditingId === r.order_id ? 'animate-spin' : ''}`}
                        />
                        Re-audit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Showing {(pagination.page - 1) * pagination.page_size + 1}–
                {Math.min(pagination.page * pagination.page_size, pagination.total)} of {pagination.total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="drift-pagination-prev"
                >
                  Previous
                </Button>
                <span className="text-xs text-gray-600 font-mono">
                  {pagination.page} / {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.pages || loading}
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  data-testid="drift-pagination-next"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
